import { Archive, RotateCcw, Search, SlidersHorizontal } from "lucide-react";
import { DateInput } from "@/components/ui/DateInput";
import { Button } from "@/components/ui/Button";
import type { ContratoDto, FiltroTipoAtivo, MaquinarioDto, PeriodRange } from "@/services/locacoes";
import { FILTRO_TIPO_OPTIONS } from "@/services/locacoes";
import { LocacoesScopeFilter, type LocacoesScopeValue } from "./LocacoesScopeFilter";

interface Props {
  period: PeriodRange;
  onPeriodChange: (period: PeriodRange) => void;
  filtroTipo: FiltroTipoAtivo;
  onFiltroTipoChange: (value: FiltroTipoAtivo) => void;
  showScope: boolean;
  contratos: ContratoDto[];
  maquinarios: MaquinarioDto[];
  scope: LocacoesScopeValue;
  onScopeChange: (scope: LocacoesScopeValue) => void;
  onApply: () => void;
  onClearScope?: () => void;
  showClearScope?: boolean;
  loading?: boolean;
  showArquivados?: boolean;
  onArquivados?: () => void;
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
  className,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  className?: string;
}) {
  const id = `loc-flt-${label.toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <div className={className ?? "min-w-[140px] max-w-[200px] flex-shrink-0"}>
      <label htmlFor={id} className="mb-1.5 block truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:text-xs">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-full cursor-pointer rounded-lg border border-slate-200 bg-slate-50 px-2.5 text-xs text-slate-800 outline-none transition-colors focus:border-brand-600 focus:ring-2 focus:ring-brand-600 sm:text-sm"
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function LocacoesFilterBar({
  period,
  onPeriodChange,
  filtroTipo,
  onFiltroTipoChange,
  showScope,
  contratos,
  maquinarios,
  scope,
  onScopeChange,
  onApply,
  onClearScope,
  showClearScope,
  loading,
  showArquivados,
  onArquivados,
}: Props) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") onApply();
  };

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-card"
      onKeyDown={handleKeyDown}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <SlidersHorizontal className="h-3.5 w-3.5 flex-shrink-0 text-brand-600" />
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Filtros</span>
        </div>
        {showArquivados && onArquivados ? (
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={onArquivados}
            leftIcon={<Archive className="h-4 w-4" />}
            className="text-slate-600 hover:text-slate-800"
          >
            Arquivados
          </Button>
        ) : null}
      </div>

      <div className="flex min-w-0 flex-wrap items-end gap-2 sm:gap-3">
        <div className="w-[118px] flex-shrink-0 sm:w-[128px]">
          <DateInput
            label="Data Inicial"
            value={period.startDate}
            onChange={(startDate) => onPeriodChange({ ...period, startDate })}
            max={period.endDate || undefined}
          />
        </div>
        <div className="w-[118px] flex-shrink-0 sm:w-[128px]">
          <DateInput
            label="Data Final"
            value={period.endDate}
            onChange={(endDate) => onPeriodChange({ ...period, endDate })}
            min={period.startDate || undefined}
          />
        </div>

        <div className="mb-0.5 hidden h-10 w-px flex-shrink-0 self-end bg-slate-200 sm:block" />

        <FilterSelect
          label="Tipo contrato"
          value={filtroTipo}
          onChange={(v) => onFiltroTipoChange(v as FiltroTipoAtivo)}
          options={FILTRO_TIPO_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          className="min-w-[130px] max-w-[180px] flex-shrink-0"
        />

        {showScope ? (
          <>
            <div className="mb-0.5 hidden h-10 w-px flex-shrink-0 self-end bg-slate-200 md:block" />
            <LocacoesScopeFilter
              variant="fields"
              contratos={contratos}
              maquinarios={maquinarios}
              filtroTipo={filtroTipo}
              scope={scope}
              onChange={onScopeChange}
            />
          </>
        ) : null}

        <div className="ml-auto flex flex-shrink-0 items-end gap-2 self-end border-l border-slate-200 pl-2 sm:pl-3">
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={onApply}
            loading={loading}
            leftIcon={<Search className="h-4 w-4" />}
          >
            Aplicar
          </Button>
          {showClearScope && onClearScope ? (
            <Button type="button" variant="ghost" size="md" onClick={onClearScope} title="Limpar escopo">
              <RotateCcw className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
